import { HTMLAttributes, RefObject } from 'react';
import { colors, r4, s16, s24, s4 } from '@ardoq/design-tokens';
import styled, { css } from 'styled-components';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { TypographyMixins, typographyMixins } from './typography';
import { focusRingCSS } from '@ardoq/style-helpers';

const underlinedElStyle = css`
  cursor: default;
  position: relative;
  display: inline-block;
`;

const underlineStyle = css`
  content: ' ';
  display: block;
  width: 100%;
  bottom: 0;
  position: absolute;
  left: 0;
  height: 2px;
`;

const underline = css`
  ${underlinedElStyle}
  &::after {
    ${underlineStyle}
  }
`;

type Extends<T, U extends T> = U;

type AsLinkOwnPropsBase = {
  /**
   * Whether to underline the link only on hover.
   * will be ignored if `noUnderline` is set to true.
   */
  underlineOnHover?: boolean;
  noUnderline?: boolean;
  hideIcon?: boolean;
  color?: string;
};

type LinkType = 'primary' | 'secondary' | 'dotted' | 'primaryButton';

type LinkOwnProps = AsLinkOwnPropsBase & {
  ref?: RefObject<HTMLAnchorElement | null>;
  href?: string;
  target?: '_blank' | '_self';
  typography?: Extends<
    TypographyMixins,
    'text1' | 'text2' | 'caption' | 'text2Bold'
  >;
  type?: LinkType;
  rel?: string;
  iconName?: IconName;
  /** When `true`, `onClick` runs alongside default `href` navigation instead of replacing it. */
  keepDefaultNavigation?: boolean;
};

export type LinkProps = LinkOwnProps & HTMLAttributes<HTMLSpanElement>;

const asLinkUnderline = (color: string, type: LinkType = 'primary') => css`
  text-decoration: underline ${type === 'dotted' ? 'dotted' : 'solid'} ${color}
    1px;
  text-underline-offset: 3px;
  padding-bottom: 3px;
  margin-bottom: -3px;
`;

const linkStyledIconCss = css`
  display: inline;
  min-width: fit-content;
  vertical-align: middle;
`;

const initialColorsByType: Record<LinkType, string> = {
  primary: colors.textAction,
  secondary: colors.textModerate,
  dotted: colors.textModerate,
  primaryButton: colors.textOnInvertedStrong,
};

const initialBackgroundColorsByType: Partial<Record<LinkType, string>> = {
  primaryButton: colors.surfaceActionPrimary,
};

const hoverColorsByType: Record<LinkType, string> = {
  primary: colors.textActionHover,
  secondary: colors.textDefault,
  dotted: colors.textDefault,
  primaryButton: colors.textOnInvertedStrong,
};

const hoverBackgroundColorsByType: Partial<Record<LinkType, string>> = {
  primaryButton: colors.surfaceActionPrimaryHover,
};

const asLinkButton = (type: LinkType = 'primary') => css`
  background: ${type ? initialBackgroundColorsByType[type] : underline};
  padding: ${s4} ${s16};
  border: 1px solid transparent;
  border-radius: ${r4};
  line-height: ${s24};
  font-weight: 500;
  &:hover {
    background: ${type ? hoverBackgroundColorsByType[type] : underline};
  }
`;

const isLinkButton = (type: LinkType = 'primary') =>
  ['primaryButton'].includes(type);

const AsLinkTextContainer = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
`;

type LinkOwnStyleProps = {
  $typography?: 'text1' | 'text2' | 'caption' | 'text2Bold';
  $type?: LinkType;
  $underlineOnHover?: boolean;
  $noUnderline?: boolean;
  $color?: string;
};

const AsLinkContainer = styled.a<LinkOwnStyleProps>`
  max-width: 100%;
  align-items: center;
  cursor: pointer;

  position: relative;
  &:focus {
    outline: none;
  }

  ${({ $color, $type }) => css`
    color: ${$color ??
    ($type ? initialColorsByType[$type] : colors.textAction)};
    &:hover {
      color: ${$color ??
      ($type ? hoverColorsByType[$type] : colors.textAction)};
    }
  `};
  ${({ $typography = 'text1' }) => typographyMixins[$typography]}
  ${({ $type = 'primary' }) => (isLinkButton($type) ? asLinkButton($type) : '')}
  ${({ $type }) =>
    $type &&
    css`
      &:focus-visible {
        outline: none;
        ${focusRingCSS};
      }
    `};

  ${({ $underlineOnHover, $noUnderline, $color, $type = 'primary' }) =>
    !isLinkButton($type) &&
    css`
      color: ${$color ?? initialColorsByType[$type]};
      ${AsLinkTextContainer} {
        ${$underlineOnHover || $noUnderline
          ? ''
          : asLinkUnderline($color ?? initialColorsByType[$type], $type)}
      }
      &:hover {
        color: ${$color ?? hoverColorsByType[$type]};
        ${AsLinkTextContainer} {
          ${!$noUnderline &&
          asLinkUnderline($color ?? hoverColorsByType[$type], $type)}
        }
      }
    `}
`;

const LinkStyledIcon = styled(Icon)`
  ${linkStyledIconCss}
  margin-left: 4px;
`;

const LinkIcon = ({
  typography,
  iconName,
}: Pick<LinkOwnProps, 'typography' | 'iconName'>) => (
  <LinkStyledIcon
    iconSize={typography === 'caption' ? (12 as IconSize) : IconSize.SMALL}
    iconName={iconName ?? IconName.OPEN_IN_NEW}
  />
);

export const Link = ({
  typography = 'text1',
  underlineOnHover,
  noUnderline,
  hideIcon,
  children,
  color,
  href,
  target,
  type = 'primary',
  rel,
  onClick,
  iconName,
  keepDefaultNavigation = false,
  ...rest
}: LinkProps) => {
  return (
    <AsLinkContainer
      {...rest}
      $color={color}
      $typography={typography}
      $type={type}
      $underlineOnHover={underlineOnHover}
      $noUnderline={noUnderline}
      target={target && href ? target : undefined}
      rel={rel && href ? rel : undefined}
      onClick={e => {
        if (onClick) {
          if (!keepDefaultNavigation) {
            e.preventDefault();
          }
          onClick(e);
        }
      }}
      href={href || '#'}
    >
      <AsLinkTextContainer>{children}</AsLinkTextContainer>
      {!hideIcon && <LinkIcon typography={typography} iconName={iconName} />}
    </AsLinkContainer>
  );
};
